import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API, { loadTokens, saveTokens, clearTokens } from './api';
import { isNative, registerForPush } from './native';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      await loadTokens();
      const { data } = await API.get('/auth/me');
      setUser(data);
      if (isNative()) registerForPush();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    const onLoggedOut = () => setUser(false);
    window.addEventListener('fpq:logged-out', onLoggedOut);
    return () => window.removeEventListener('fpq:logged-out', onLoggedOut);
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post('/auth/login', { email, password });
    await saveTokens(data);
    const profile = { ...data };
    ['access_token', 'refresh_token', 'token_type', 'expires_in'].forEach((k) => delete profile[k]);
    setUser(profile);
    if (isNative()) registerForPush();
    return profile;
  };

  const logout = async () => {
    try {
      await API.post('/auth/logout');
    } catch {
      // ignore
    }
    await clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg || JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}
